/**
 * safeStorage — thin wrappers around localStorage / sessionStorage.
 *
 * Purpose
 *   • Centralise error handling for Safari private mode + Storage quota errors.
 *   • Document at a glance that the call site stores NON-SENSITIVE UI state.
 *   • Forbid storing auth tokens / PII — the project's auth tokens are httpOnly
 *     cookies and MUST NOT touch web storage.
 *
 * What is OK to store here
 *   ✔ Daily dismissal flags     (e.g. "splash seen today")
 *   ✔ User UI preferences       (theme, language)
 *   ✔ Recent-action breadcrumbs (e.g. command-palette MRU IDs)
 *   ✔ Consent preferences       (cookie banner state)
 *   ✔ Per-tab modal state       (sessionStorage)
 *
 * What is NEVER OK to store here
 *   ✘ JWT / session tokens — use httpOnly cookies via the server
 *   ✘ Personally identifiable info (email, phone, name, address)
 *   ✘ Payment data or anything that maps to a billing identity
 *
 * If you find yourself wanting to store anything from the "never" list, stop
 * and route it through the backend instead.
 */

const NEVER_KEYS = ["token", "jwt", "session_id", "password", "auth"];

function _assertSafeKey(key) {
  const lc = String(key).toLowerCase();
  for (const banned of NEVER_KEYS) {
    if (lc.includes(banned)) {
      // Surfaces in dev console — production agents can grep for this.
      if (typeof console !== "undefined") {
        // eslint-disable-next-line no-console
        console.warn(`[safeStorage] Refused to store sensitive-looking key: ${key}. Use httpOnly cookies for auth.`);
      }
      return false;
    }
  }
  return true;
}

function _getStorage(persistent) {
  if (typeof window === "undefined") return null;
  try {
    return persistent ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function getItem(key, { persistent = true } = {}) {
  const store = _getStorage(persistent);
  if (!store) return null;
  try { return store.getItem(key); }
  catch { return null; }
}

export function setItem(key, value, { persistent = true } = {}) {
  if (!_assertSafeKey(key)) return false;
  const store = _getStorage(persistent);
  if (!store) return false;
  try { store.setItem(key, value); return true; }
  catch { return false; }
}

export function removeItem(key, { persistent = true } = {}) {
  const store = _getStorage(persistent);
  if (!store) return;
  try { store.removeItem(key); } catch { /* ignore */ }
}

export function getJSON(key, fallback = null, opts) {
  const raw = getItem(key, opts);
  if (raw == null) return fallback;
  try { return JSON.parse(raw); }
  catch { return fallback; }
}

export function setJSON(key, value, opts) {
  try { return setItem(key, JSON.stringify(value), opts); }
  catch { return false; }
}
